import { useState, useEffect, useContext, useRef } from "react";
import { useMediaQuery, useTheme } from "@mui/material";
import ModalBasicLayout from "./ModalBasicLayout";
import { ConfirmAlert } from "../Alerts/Alerts";
import { useTranslationApp } from "../../lib/i18next";
import {
  useImageEdit,
  useChatMessages,
  useImageLayers,
  useAnalyzeImageLayers,
  PROMPTS_CHECKPOINT_SIZE,
} from "./ModalImageEdit/hooks";
import {
  ChatSidebar,
  ResponsiveSidebarContainer,
  WorkModeSelector,
} from "./ModalImageEdit/shared";
import { ImageCanvas, SingleChatMessages } from "./ModalImageEdit/single";
import LayerCanvas from "./ModalImageEdit/layers/LayerCanvas";
import LayersSidebar from "./ModalImageEdit/layers/LayersSidebar";
import { IMAGE_EDIT_WORK_MODE } from "./ModalImageEdit/layers/layerGeometry";
import { composeLayersToBlob } from "./ModalImageEdit/layers/composeLayers";
import { useDispatchApp, useSelectorApp } from "../../lib/redux";
import { startGetBrandInformation } from "../../actions/adsCampaigns";
import { getIsBlockedActions } from "../../actions/getters";
import { getPathDatabaseByRole } from "../../actions/auth";
import { useCreditActionCosts } from "../../hooks/useCreditActionCosts";
import { validateModalImageEditTextFields } from "../../pages/Settings/BrandSetup/utils/blockedBrandsValidation";
import {
  BUTTON_CONTINUE,
  BUTTON_GO_BACK,
  MODAL_IMAGE_EDIT_TITLE,
  MODAL_IMAGE_EDIT_AI_LOSES_LAYERS_TITLE,
  MODAL_IMAGE_EDIT_AI_LOSES_LAYERS_DESCRIPTION,
} from "../../i18n/keysTranslations";
import { UploadFilesContext } from "../../contexts/UploadFilesContext";

/**
 * Main modal for AI-assisted image editing
 */
const ModalImageEdit = ({
  isOpen,
  onClose,
  initialImageUrl = null,
  onCallbackSave = () => {},
  showSaveAlert = true,
  initialWorkMode = IMAGE_EDIT_WORK_MODE.GENERATIVE,
  jobID,
  alertStatus,
  generationData,
  contentID,
  isCarousel = false,
  carouselIndex,
}) => {
  const { ADS_CAMPAIGN_GENERATE_CREATIVES_ACTION } = useCreditActionCosts();
  const theme = useTheme();
  const { t } = useTranslationApp();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useDispatchApp();
  const { uploadFile } = useContext(UploadFilesContext) || {};
  const { userID } = dispatch(getPathDatabaseByRole());
  const isPlanActive = useSelectorApp((state) => state?.payments?.isPlanActive);
  const isTrial = useSelectorApp(
    (state) => state?.payments?.subscriptionPlanInfo?.isTrial
  );

  const resolvedInitialImageUrl =
    initialImageUrl ||
    generationData?.sourceUrl ||
    generationData?.url ||
    null;

  const [imageUrl, setImageUrl] = useState(resolvedInitialImageUrl);
  /** Base image URL for the current checkpoint block (sent to the API as `url`) */
  const [blockBaseImageUrl, setBlockBaseImageUrl] = useState(
    resolvedInitialImageUrl
  );
  /** User prompts accumulated since the last checkpoint (successful edits only) */
  const [checkpointPromptsHistory, setCheckpointPromptsHistory] = useState([]);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [businessLookAndFeel, setBusinessLookAndFeel] = useState("");
  const [workMode, setWorkMode] = useState(
    initialWorkMode || IMAGE_EDIT_WORK_MODE.GENERATIVE
  );

  const { chatMessages, addMessage, replaceThinkingMessage, chatScrollRef } =
    useChatMessages();
  const {
    isProcessing,
    isEditing,
    editImage,
    createThinkingMessage,
    resetAttachmentAccumulation,
  } = useImageEdit();
  const isLayersMode = workMode === IMAGE_EDIT_WORK_MODE.LAYERS;
  const {
    layers,
    canvasWidth,
    canvasHeight,
    layersDirty,
    selectedLayerId,
    setSelectedLayerId,
    updateLayerBox,
    addLayersFromUrls,
    reorderLayers,
    renameLayer,
    persistSnapshotNow,
    hasSnapshot,
    isLoadingSnapshot,
  } = useImageLayers({
    sourceUrl: imageUrl,
    enabled: isOpen,
  });
  const acknowledgedLayerLossForUrlRef = useRef(null);
  const resolvedContentID = contentID || generationData?.contentID || null;
  const resolvedIsCarousel = Boolean(isCarousel || generationData?.isCarousel);
  const resolvedCarouselIndex =
    carouselIndex ?? generationData?.carouselIndex;

  const { analyze, isAnalyzing, errorMessage } = useAnalyzeImageLayers({
    sourceUrl: imageUrl,
    alertStatus,
    jobID,
    contentID: resolvedContentID,
    isCarousel: resolvedIsCarousel,
    carouselIndex: resolvedCarouselIndex,
  });

  const toggleSidebar = () => setIsSidebarOpen((prev) => !prev);

  const currentImageHasLayerAnalysis = hasSnapshot || layers.length > 0;

  const confirmAiEditLosesLayers = async () => {
    if (!currentImageHasLayerAnalysis) return true;
    if (acknowledgedLayerLossForUrlRef.current === imageUrl) return true;

    const result = await ConfirmAlert({
      title: t(MODAL_IMAGE_EDIT_AI_LOSES_LAYERS_TITLE),
      text: t(MODAL_IMAGE_EDIT_AI_LOSES_LAYERS_DESCRIPTION),
      confirmButtonText: t(BUTTON_CONTINUE),
      cancelButtonText: t(BUTTON_GO_BACK),
      icon: "warning",
    });

    if (!result?.isConfirmed) return false;
    acknowledgedLayerLossForUrlRef.current = imageUrl;
    return true;
  };

  const onWorkModeChange = async (nextMode) => {
    if (isAnalyzing && nextMode === IMAGE_EDIT_WORK_MODE.GENERATIVE) {
      return;
    }
    if (
      nextMode === IMAGE_EDIT_WORK_MODE.GENERATIVE &&
      workMode === IMAGE_EDIT_WORK_MODE.LAYERS
    ) {
      const canContinue = await confirmAiEditLosesLayers();
      if (!canContinue) return;
    }
    setWorkMode(nextMode);
  };

  /**
   * Gets brand information including businessLookAndFeel
   */
  const getBrandInformation = async () => {
    const response = await dispatch(startGetBrandInformation());
    if (response.ok) {
      const { data } = response;
      setBusinessLookAndFeel(data?.businessLookAndFeel || "");
    }
  };

  /**
   * Handles sending a message to edit the image
   */
  const onSendMessage = async (
    prompt,
    attachmentUrls = [],
    attachmentFileNames = {}
  ) => {
    const trimmedPrompt = prompt.trim();
    if (!trimmedPrompt || isProcessing || !blockBaseImageUrl) return;
    try {
      validateModalImageEditTextFields({
        prompt: trimmedPrompt,
        isPlanActive,
        isTrial,
      });
    } catch (error) {
      addMessage({
        id: Date.now().toString(),
        role: "assistant",
        content: `Error: ${error?.message || "The request could not be processed."}`,
        timestamp: new Date(),
        isError: true,
      });
      return;
    }

    const canContinue = await confirmAiEditLosesLayers();
    if (!canContinue) return;

    const isBlocked = await dispatch(
      getIsBlockedActions({
        action: ADS_CAMPAIGN_GENERATE_CREATIVES_ACTION,
        showUsageConfirmationAlert: true,
        multiplier: 1,
      })
    );
    if (isBlocked) return;

    const promptsOrdered = [...checkpointPromptsHistory, trimmedPrompt];

    const userMessage = {
      id: Date.now().toString(),
      role: "user",
      content: trimmedPrompt,
      timestamp: new Date(),
      imageUrl: imageUrl,
      attachmentUrls: Array.isArray(attachmentUrls) ? attachmentUrls : [],
      attachmentFileNames: attachmentFileNames || {},
    };

    addMessage(userMessage);

    const thinkingMessage = createThinkingMessage();
    addMessage(thinkingMessage);

    await editImage(
      blockBaseImageUrl,
      promptsOrdered,
      businessLookAndFeel,
      Array.isArray(attachmentUrls) ? attachmentUrls : [],
      attachmentFileNames || {},
      // onSuccess
      (newImageUrl, aiResponse) => {
        setImageUrl(newImageUrl);
        replaceThinkingMessage(aiResponse);

        const completedBlockSize = promptsOrdered.length;
        if (completedBlockSize >= PROMPTS_CHECKPOINT_SIZE) {
          setBlockBaseImageUrl(newImageUrl);
          setCheckpointPromptsHistory([]);
          resetAttachmentAccumulation();
        } else {
          setCheckpointPromptsHistory(promptsOrdered);
        }
      },
      // onError
      (errorMessageText) => {
        replaceThinkingMessage(errorMessageText);
      }
    );
  };

  /**
   * Handles selecting an image from messages
   */
  const onImageSelect = (selectedImageUrl) => {
    setImageUrl(selectedImageUrl);
  };

  const onAddLayerUrls = async (urls) => {
    await addLayersFromUrls(urls);
  };

  const onSaveLayeredComposition = async (payload) => {
    if (typeof uploadFile !== "function") {
      await onCallbackSave(payload);
      return;
    }

    const blob = await composeLayersToBlob({
      layers,
      canvasWidth,
      canvasHeight,
    });
    const file = new File([blob], "layered-image.png", { type: "image/png" });
    const uploadedUrl = await uploadFile({
      file,
      pathStorage: `socialMedia/${userID}/`,
      doCompression: false,
    });
    if (!uploadedUrl) return;
    await persistSnapshotNow(uploadedUrl);
    await onCallbackSave({ ...payload, url: uploadedUrl });
  };

  // Sync canvas, block base URL, and checkpoint state when the parent passes a new asset
  useEffect(() => {
    if (initialImageUrl || generationData?.sourceUrl || generationData?.url) {
      const nextUrl =
        initialImageUrl ||
        generationData?.sourceUrl ||
        generationData?.url;
      setImageUrl(nextUrl);
      setBlockBaseImageUrl(nextUrl);
      setCheckpointPromptsHistory([]);
      resetAttachmentAccumulation();
    }
  }, [
    generationData?.sourceUrl,
    generationData?.url,
    initialImageUrl,
    resetAttachmentAccumulation,
  ]);

  useEffect(() => {
    if (isOpen) {
      setIsSidebarOpen(true);
      setWorkMode(initialWorkMode || IMAGE_EDIT_WORK_MODE.GENERATIVE);
    }
  }, [isOpen, initialWorkMode]);

  useEffect(() => {
    acknowledgedLayerLossForUrlRef.current = null;
  }, [imageUrl]);

  // Get brand information on mount
  useEffect(() => {
    getBrandInformation();
    // eslint-disable-next-line
  }, []);

  return (
    <ModalBasicLayout
      modalOpen={isOpen}
      onCloseModal={onClose}
      fullScreen={true}
      maxWidth={false}
      fullWidth={true}
      hasTitle={false}
      ariaLabel={t(MODAL_IMAGE_EDIT_TITLE)}
      sxContent={{
        py: 0,
        px: 0,
        display: "flex",
        flexDirection: "row",
        height: "100vh",
        maxHeight: "100vh",
        overflow: "hidden",
      }}
      sxPaper={{
        height: "100vh",
        maxHeight: "100vh",
      }}
    >
      <ResponsiveSidebarContainer
        isSmallScreen={isSmallScreen}
        open={isSidebarOpen}
        onClose={toggleSidebar}
      >
        {isSidebarOpen && (
          <ChatSidebar
            isSmallScreen={isSmallScreen}
            title={t(MODAL_IMAGE_EDIT_TITLE)}
            isProcessing={isProcessing}
            onSendMessage={onSendMessage}
            onToggleSidebar={toggleSidebar}
            scrollRef={chatScrollRef}
            hideInput={isLayersMode}
            headerExtra={
              <WorkModeSelector
                selectedValue={workMode}
                disabled={isAnalyzing}
                onChange={onWorkModeChange}
              />
            }
          >
            {isLayersMode ? (
              <LayersSidebar
                layers={layers}
                selectedLayerId={selectedLayerId}
                onSelectLayer={setSelectedLayerId}
                isAnalyzing={isAnalyzing}
                isLoadingSnapshot={isLoadingSnapshot}
                errorMessage={hasSnapshot ? null : errorMessage}
                onAnalyze={hasSnapshot ? null : analyze}
                onAddLayerUrls={onAddLayerUrls}
                onReorderLayers={reorderLayers}
                onRenameLayer={renameLayer}
              />
            ) : (
              <SingleChatMessages
                messages={chatMessages}
                currentImageUrl={imageUrl}
                onImageSelect={onImageSelect}
              />
            )}
          </ChatSidebar>
        )}
      </ResponsiveSidebarContainer>

      {isLayersMode ? (
        <LayerCanvas
          imageUrl={imageUrl}
          initialImageUrl={resolvedInitialImageUrl}
          layers={layers}
          canvasWidth={canvasWidth}
          canvasHeight={canvasHeight}
          isAnalyzing={!hasSnapshot && isAnalyzing}
          selectedLayerId={selectedLayerId}
          onSelectLayer={setSelectedLayerId}
          onLayerBoxChange={updateLayerBox}
          isSidebarOpen={isSidebarOpen}
          onToggleSidebar={toggleSidebar}
          onClose={onClose}
          onCallbackSave={onSaveLayeredComposition}
          showSaveAlert={showSaveAlert}
          saveDisabled={!layersDirty}
        />
      ) : (
        <ImageCanvas
          imageUrl={imageUrl}
          initialImageUrl={resolvedInitialImageUrl}
          isEditing={isEditing}
          isSidebarOpen={isSidebarOpen}
          onToggleSidebar={toggleSidebar}
          onClose={onClose}
          onCallbackSave={onCallbackSave}
          showSaveAlert={showSaveAlert}
        />
      )}
    </ModalBasicLayout>
  );
};

export default ModalImageEdit;
